import { IProductInfo } from '@app/core/product/models/product-info.model';
import { IProduct } from '@app/core/product/models/product.model';
import { hexGenerate } from '@roller/rdl/utilities/string';
import { IBookingItem, IBookingItemModifier } from './tickets.model';

/** Finds and links the product / parent product with the bookingItem. Also populates the modifiers for display in the booking summary. */
export function linkProductAndParentProduct(item: IBookingItem, products: IProduct[]): IBookingItem {
  const parentProduct = products.find((product: IProduct) => product.id === item.parentProductId.toString());
  item.product = parentProduct?.products.find((product: IProductInfo) => product.id === item.productId.toString());
  if (item.modifiers && item.product) {
    item.product.bookingModifiers = item.modifiers.map((modifier: IBookingItemModifier) => {
      if (modifier.amount) {
        modifier.totalModifierAmount =
          (modifier.quantity || 1) * item.quantity * (modifier.modifierAmount || modifier.amount || 1);
      }

      return modifier;
    });
  }

  return item;
}

/** Link related items together based on associatedBookingId and date/session */
export function linkRelatedBookingItems(item: IBookingItem, list: IBookingItem[]): IBookingItem {
  let relatedOrderItemKey = hexGenerate(15);
  if (item.relatedOrderItemKey || !item.product) {
    item.relatedOrderItemKey = item.relatedOrderItemKey || relatedOrderItemKey;
    return item;
  }

  // find related Item for addons
  if (item.associatedBookingItemId) {
    // this is an addon - find its associatedBooking Item
    const findAssociatedItem = list.find((bItem) => bItem.bookingItemId === item.associatedBookingItemId);
    if (findAssociatedItem) {
      if (!findAssociatedItem.relatedOrderItemKey) {
        findAssociatedItem.relatedOrderItemKey = hexGenerate(15);
      }

      item.relatedOrderItemKey = findAssociatedItem.relatedOrderItemKey;
      item.addonToProductId = findAssociatedItem.product?.id;
      item.sessionName = item.sessionName || findAssociatedItem.sessionName;
      item.sessionEnd = item.sessionEnd || findAssociatedItem.sessionEnd;
      item.sessionStart = item.sessionStart || findAssociatedItem.sessionStart;
    }
  } else {
    let relatedPeers: IBookingItem[];
    // try to find any peers, sessionBased, dateBased, other
    if (item.product.isItemSessionBased && item.sessionStart) {
      relatedPeers = list.filter(
        (bItem) =>
          !bItem.associatedBookingItemId &&
          bItem.bookingItemId !== item.bookingItemId &&
          bItem.parentProductId === item.parentProductId &&
          bItem.bookingDate === item.bookingDate &&
          bItem.sessionStart === item.sessionStart &&
          bItem.sessionEnd === item.sessionEnd
      );
    } else if (item.product.isItemDateBased) {
      relatedPeers = list.filter(
        (bItem) =>
          !bItem.associatedBookingItemId &&
          bItem.bookingItemId !== item.bookingItemId &&
          bItem.parentProductId === item.parentProductId &&
          bItem.bookingDate === item.bookingDate
      );
    } else {
      relatedPeers = list.filter(
        (bItem) =>
          !bItem.associatedBookingItemId &&
          bItem.bookingItemId !== item.bookingItemId &&
          bItem.parentProductId === item.parentProductId &&
          bItem.bookingDate === item.bookingDate
      );
    }

    // If there is a related peer that already has a key, then use that to populate the same key for all the related booking items.
    const findRelatedPeerWithKey = relatedPeers.find((bItem) => bItem.relatedOrderItemKey);
    if (findRelatedPeerWithKey?.relatedOrderItemKey) {
      relatedOrderItemKey = findRelatedPeerWithKey.relatedOrderItemKey;
    }

    relatedPeers.forEach((bItem) => (bItem.relatedOrderItemKey = relatedOrderItemKey));
    item.relatedOrderItemKey = relatedOrderItemKey;
  }

  return item;
}

/** Sort the non-addons first in the list, with non forced items first */
export function sortBookingItems(itemA: IBookingItem, itemB: IBookingItem): number {
  if (itemA.associatedBookingItemId && !itemB.associatedBookingItemId) {
    return 1;
  }

  if (itemB.associatedBookingItemId && !itemA.associatedBookingItemId) {
    return -1;
  }

  if (!itemA.associatedBookingItemId && !itemB.associatedBookingItemId) {
    return itemA.product?.forceMinPurchase ? 1 : -1;
  }

  return itemA.name.localeCompare(itemB.name);
}
